import { Box, Button, Card, CardContent, Chip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowRight, Rocket, Target, TrendingUp, Zap } from 'lucide-react';
import React from 'react';

interface LearningPathWidgetProps {
    onTabChange: (tabIndex: number) => void;
}

function LearningPathWidget({ onTabChange }: LearningPathWidgetProps) {
    const theme = useTheme();

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `2px solid ${theme.palette.primary.main}`,
                background: 'transparent',
                flex: 1,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                },
            }}
            onClick={() => onTabChange(5)} // T4lo Missions tab
        >
            <CardContent sx={{ p: { xs: 2, md: 2, lg: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                    >
                        <Target size={20} color={theme.palette.primary.main} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    fontSize: '0.9rem',
                                }}
                            >
                                Dein Lernpfad wartet auf dich!
                            </Typography>
                            <Chip
                                label="Neu"
                                size="small"
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: 'white',
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                }}
                            />
                        </Box>

                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                mb: 2,
                                lineHeight: 1.5,
                            }}
                        >
                            Starte deine persönliche Entwicklungsreise mit maßgeschneiderten Missionen, die deine Skills
                            auf das nächste Level bringen.
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Zap size={14} color={theme.palette.warning.main} />
                                <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
                                >
                                    Skill Boosts
                                </Typography>
                            </Box>
                            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: theme.palette.divider }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendingUp size={14} color={theme.palette.success.main} />
                                <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
                                >
                                    Karriere Ziele
                                </Typography>
                            </Box>
                            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: theme.palette.divider }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Rocket size={14} color={theme.palette.info.main} />
                                <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
                                >
                                    Challenges
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto', pt: 1 }}>
                            <Button
                                variant="contained"
                                size="small"
                                endIcon={<ArrowRight size={14} />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    borderRadius: 1.5,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Lernpfad erstellen
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default LearningPathWidget;
